import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, ArrowRight, CreditCard, CheckCircle2, AlertCircle, Settings as Cog, Minus, Plus, ShieldCheck, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useBillingStatus } from "../lib/billing";
import { useAuth } from "../context/AuthContext";
import InviteTeammateModal from "../components/InviteTeammateModal";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
}

const STATUS_MAP = {
  active:     { label: "Active", cls: "badge-lime" },
  trialing:   { label: "Trial",  cls: "badge-blue" },
  past_due:   { label: "Past Due", cls: "badge-red" },
  canceled:   { label: "Canceled", cls: "badge-mute" },
  incomplete: { label: "Setup incomplete", cls: "badge-orange" },
  comped:     { label: "Comped · free paid", cls: "badge-lime" },
};

export default function Billing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { billing, loading, refresh } = useBillingStatus();
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const isAdmin = user?.role === "admin";

  // After Stripe redirects back with ?session_id=…, poll for status
  useEffect(() => {
    const sid = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast.message("Checkout canceled");
      const p = new URLSearchParams(params); p.delete("canceled"); setParams(p, { replace: true });
      return;
    }
    if (!sid) return;
    let cancelled = false;
    const poll = async (attempts = 0) => {
      if (attempts > 6 || cancelled) return;
      try {
        const { data } = await api.get(`/billing/checkout/${sid}`);
        if (data.payment_status === "paid" || data.subscription_status === "active" || data.subscription_status === "trialing") {
          toast.success("Subscription active 🎉");
          await refresh();
          const p = new URLSearchParams(params); p.delete("session_id"); setParams(p, { replace: true });
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(() => poll(attempts + 1), 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const startCheckout = async () => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  const openPortal = async () => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open billing portal");
      setBusy(false);
    }
  };

  // Seat preview — declared before any conditional return to satisfy rules-of-hooks
  const liveSeats = billing?.seats || 1;
  const pricePerSeat = billing?.price_per_seat_usd || 29;
  const [previewSeats, setPreviewSeats] = useState(liveSeats);
  useEffect(() => { setPreviewSeats(liveSeats); }, [liveSeats]);
  const previewMonthly = useMemo(() => pricePerSeat * previewSeats, [previewSeats, pricePerSeat]);
  const previewDelta = previewSeats - liveSeats;
  const [inviteOpen, setInviteOpen] = useState(false);

  if (loading || !billing) {
    return (
      <div data-testid="billing-page">
        <div className="stratus-page-header">
          <div>
            <div className="stratus-page-title">Billing</div>
            <div className="stratus-page-sub">Loading…</div>
          </div>
        </div>
      </div>
    );
  }

  const status = billing.subscription_status;
  const seats = liveSeats;
  const monthly = pricePerSeat * seats;
  const statusInfo = status ? STATUS_MAP[status] : null;
  const showTrialBanner = billing.in_trial && !billing.has_subscription;
  const stripeReady = billing.stripe_configured !== false;

  return (
    <div data-testid="billing-page">
      <div className="stratus-page-header">
        <div>
          <div className="stratus-page-title" data-testid="page-title">Billing</div>
          <div className="stratus-page-sub">
            {seats} active seat{seats === 1 ? "" : "s"} · ${billing.price_per_seat_usd.toFixed(2)} per seat / month
          </div>
        </div>
      </div>

      {billing.comped && (
        <div className="trial-card" data-testid="billing-comped-card">
          <div className="trial-card-icon" style={{ background: "var(--pg-lime-soft, rgba(150, 220, 80, 0.18))", color: "var(--pg-lime, #6aa84f)" }}>
            <Gift />
          </div>
          <div className="trial-card-body">
            <div className="trial-card-title">
              Your workspace is comped by Pragma — free paid plan
              {billing.comped_until ? ` until ${fmtDate(billing.comped_until)}` : " · no end date"}
            </div>
            <div className="trial-card-sub">
              {billing.comped_reason ? `Reason: ${billing.comped_reason}.` : null}
              {" "}You have full paid features at no cost. No card on file is required.
            </div>
          </div>
        </div>
      )}

      {showTrialBanner && !billing.comped && (
        <div className="trial-card" data-testid="billing-trial-card">
          <div className="trial-card-icon" style={{ background: "var(--pg-purple-soft)", color: "var(--pg-purple)" }}>
            <Sparkles />
          </div>
          <div className="trial-card-body">
            <div className="trial-card-title">
              You're in your free trial · {billing.trial_days_left} day{billing.trial_days_left === 1 ? "" : "s"} left
            </div>
            <div className="trial-card-sub">
              No card required. Trial ends {fmtDate(billing.trial_end)}. Add a payment method any time to keep your workspace running.
            </div>
          </div>
          {isAdmin && (
            <button className="btn-pill-primary" onClick={startCheckout} disabled={busy} data-testid="billing-start-checkout-trial">
              {busy ? "Opening…" : "Add payment method"} <ArrowRight strokeWidth={2.5} />
            </button>
          )}
        </div>
      )}

      <div className="billing-grid">
        <div className="billing-card" data-testid="billing-plan-card">
          <div className="billing-card-eyebrow">Your plan</div>
          <div className="billing-plan-name">Pragma — Per Seat</div>
          <div className="billing-plan-price">
            <span className="billing-price-amount">${monthly.toFixed(2)}</span>
            <span className="billing-price-suffix"> / month</span>
          </div>
          <div className="billing-plan-meta">
            {seats} × ${billing.price_per_seat_usd.toFixed(2)} / seat
          </div>

          {statusInfo && (
            <div className="billing-status-row">
              <span className={`stat-badge ${statusInfo.cls}`} data-testid="billing-status-badge">{statusInfo.label}</span>
              {billing.current_period_end && (
                <span className="billing-meta">Renews {fmtDate(billing.current_period_end)}</span>
              )}
            </div>
          )}

          <ul className="billing-plan-feats">
            <li><CheckCircle2 /> Unlimited leads, deals, contacts, companies</li>
            <li><CheckCircle2 /> Multi-pipeline Kanban + custom fields</li>
            <li><CheckCircle2 /> Global search, notifications & overdue tracking</li>
            <li><CheckCircle2 /> Branded white-label workspace</li>
          </ul>

          {/* ─── Team size + invite ─── */}
          <div className="billing-seat-preview" data-testid="billing-seat-preview">
            <div className="billing-seat-preview-row">
              <div className="billing-seat-preview-label">Team size</div>
              <div className="billing-seat-stepper" role="group" aria-label="Adjust team size">
                <button
                  type="button"
                  className="billing-seat-step"
                  onClick={() => setPreviewSeats((n) => Math.max(1, n - 1))}
                  disabled={previewSeats <= 1}
                  aria-label="Preview removing seat"
                  data-testid="billing-seat-decrement"
                  title="Preview only. To remove a seat, deactivate a user in Settings → Team."
                >
                  <Minus strokeWidth={2.5} />
                </button>
                <span className="billing-seat-count" data-testid="billing-seat-preview-count">{previewSeats}</span>
                <button
                  type="button"
                  className="billing-seat-step billing-seat-step-primary"
                  onClick={() => {
                    if (!isAdmin) return;
                    // Reset preview to current so the modal always shows +1 from live state
                    setPreviewSeats(liveSeats);
                    setInviteOpen(true);
                  }}
                  disabled={!isAdmin}
                  aria-label="Invite teammate"
                  data-testid="billing-seat-increment"
                >
                  <Plus strokeWidth={2.5} />
                </button>
              </div>
            </div>
            <div className="billing-seat-preview-total" data-testid="billing-seat-preview-total">
              {previewDelta === 0 ? (
                <>Currently <strong>${previewMonthly.toFixed(2)} / month</strong>. Press <strong>+</strong> to invite a teammate.</>
              ) : previewDelta > 0 ? (
                <>Preview only: {previewDelta} more seat{previewDelta === 1 ? "" : "s"} would be <strong>${previewMonthly.toFixed(2)} / month</strong></>
              ) : (
                <>Preview only: {Math.abs(previewDelta)} fewer seat{Math.abs(previewDelta) === 1 ? "" : "s"} would be <strong>${previewMonthly.toFixed(2)} / month</strong></>
              )}
            </div>
          </div>

          {!stripeReady && (
            <div className="billing-readonly-note" data-testid="billing-stripe-not-configured" style={{ marginBottom: 14 }}>
              <strong>Stripe is not yet connected to this workspace.</strong><br />
              Your trial keeps running. To enable card payments, your platform admin needs to add a Stripe Secret Key to the backend `.env` (STRIPE_API_KEY).
            </div>
          )}

          {isAdmin ? (
            billing.comped ? (
              <div className="billing-readonly-note" data-testid="billing-comped-note">
                <strong>Comped by Pragma.</strong> Your workspace runs on a free paid plan — no card or subscription needed.
              </div>
            ) : billing.has_subscription ? (
              <button className="btn-pill-primary" onClick={openPortal} disabled={busy || !stripeReady} data-testid="billing-manage">
                <Cog strokeWidth={2.5} /> {busy ? "Opening…" : "Manage subscription"}
              </button>
            ) : (
              <button className="btn-pill-primary" onClick={startCheckout} disabled={busy || !stripeReady} data-testid="billing-start-checkout">
                <CreditCard strokeWidth={2.5} /> {busy ? "Opening…" : (stripeReady ? "Start subscription" : "Stripe not connected")}
              </button>
            )
          ) : (
            <div className="billing-readonly-note">
              Only workspace Admins can change billing. Ask your admin to manage the subscription.
            </div>
          )}

          <div className="billing-reassurance" data-testid="billing-reassurance">
            <ShieldCheck strokeWidth={2} /> Cancel anytime. We'll prorate the unused days back to your card.
          </div>
        </div>

        <div className="billing-card" data-testid="billing-invoices-card">
          <div className="billing-card-eyebrow">Invoices & payment method</div>
          <div className="billing-invoice-body">
            <AlertCircle />
            <div>
              <div className="billing-plan-meta" style={{ color: "var(--pg-text)" }}>
                Stripe handles invoices for you.
              </div>
              <div className="billing-meta" style={{ marginTop: 6 }}>
                Click <strong>Manage subscription</strong> to download invoices, update your card, change billing email, or cancel.
              </div>
            </div>
          </div>
          {isAdmin && billing.has_subscription && (
            <button className="ghost-pill" onClick={openPortal} disabled={busy} data-testid="billing-portal-secondary">
              Open Stripe portal <ArrowRight strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>

      <InviteTeammateModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        currentSeats={liveSeats}
        onInvited={async () => { await refresh(); }}
      />
    </div>
  );
}
